import type { SidebarRightState } from '../stores/sidebar'
import type { ElementalFormData } from '../types/elemental'

interface RouterQueryParamsOptions {
  'sbarr': Omit<SidebarRightState, 'currentElemental'>
  'sbarr_f': {
    field: keyof ElementalFormData | 'title'
    section: string
  }
  'dial': {
    id: string
  }
}

type DomainKey<Domain extends keyof RouterQueryParamsOptions> = keyof RouterQueryParamsOptions[Domain] extends string ? `${Domain}_${keyof RouterQueryParamsOptions[Domain]}` : never

function parseOptionToQuery(domain: keyof RouterQueryParamsOptions, query: Partial<RouterQueryParamsOptions[typeof domain]>): Record<DomainKey<typeof domain>, unknown> {
  const result = {} as Record<DomainKey<typeof domain>, RouterQueryParamsOptions[typeof domain]>
  for (const key in query) {
    const domainKey = `${domain}_${key}` as DomainKey<typeof domain>
    // @ts-expect-error - key is a valid key of query
    if (query[key] === null || query[key] === undefined)
      continue
    // @ts-expect-error - key is a valid key of query
    if (Array.isArray(query[key]) && query[key].length > 0) {
      // @ts-expect-error - key is a valid key of query
      result[domainKey] = query[key].join(',')
      continue
    }

    // @ts-expect-error - key is a valid key of query
    result[domainKey] = query[key] as RouterQueryParamsOptions[typeof domain]
  }
  return result
}

export const useRouterQueryParams = () => {
  const route = useRoute()
  const router = useRouter()
  const query = computed(() => route.query)
  const setQuery = (domain: keyof RouterQueryParamsOptions, query: Partial<RouterQueryParamsOptions[typeof domain]>) => {
    const newQuery = parseOptionToQuery(domain, query)
    const newQueryParams = {
      ...route.query,
      ...newQuery,
    }

    router.push({
      query: newQueryParams,
    })
  }

  const resetQuery = (domain: keyof RouterQueryParamsOptions) => {
    const newQuery = Object.keys(route.query).reduce((acc, key) => {
      if (!key.startsWith(domain)) {
        return {
          ...acc,
          [key]: route.query[key],
        }
      }
      return acc
    }, {} as Record<string, unknown>)

    router.push({
      // @ts-expect-error - newQuery is a valid query
      query: newQuery,
    })
  }

  const watchQueryParam = <Domain extends keyof RouterQueryParamsOptions, T>(domainKey: DomainKey<Domain>,
    parseValue?: (value: unknown) => T,
  ) => {
    return computed(() => {
      if (route.query[domainKey] === undefined)
        return null
      if (parseValue)
        return parseValue(route.query[domainKey])

      return route.query[domainKey]
    })
  }
  return {
    query,
    setQuery,
    watchQueryParam,
    resetQuery,
  }
}

export const useQueryParams = <Domain extends keyof RouterQueryParamsOptions, T>(domain: Domain, path: keyof RouterQueryParamsOptions[typeof domain], parseValue?: (value: unknown) => T) => {
  const route = useRoute()
  const router = useRouter()
  // @ts-expect-error - domain is a valid key of RouterQueryParamsOptions
  const domainKey = `${domain}_${path}` as DomainKey<Domain>

  const value = computed(() => {
    if (route.query[domainKey] === undefined)
      return null
    if (parseValue)
      return parseValue(route.query[domainKey]) as ReturnType<typeof parseValue>

    return route.query[domainKey]
  })
  const setQuery = (value: RouterQueryParamsOptions[typeof domain][typeof path] | null | undefined) => {
    const newQuery = {
      [domainKey]: value,
    }
    const newQueryParams = {
      ...route.query,
      ...newQuery,
    }

    router.push({
      // @ts-expect-error - newQueryParams is a valid query
      query: newQueryParams,
    })
  }

  return [
    value,
    setQuery,
  ] as const
}
